import { startOfDay } from 'date-fns';
import uniqBy from 'lodash/uniqBy';

import { CashFlowConfig } from '@ndvr/portfolio-api';
import { ProjectedIncomeSummary } from '@ndvr/reporting-api';

import { getPolicyWithdrawals } from 'views/account/new-account/investment-policy-overview';
import { upcomingPolicyWithdrawals } from 'views/account/new-account/policy-withdrawals';
import { AccountGroupProfileWithAumIncluded, AccountWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { isReportableAccount } from 'views/portfolio/reporting-queries';

import { toNumber } from '../reporting/format';

// Row order of the projected income table, matching the design: fixed income first, then
// equities, then cash. Categories the report does not know about are appended after these.
export const INCOME_CATEGORY_ORDER = ['Bond', 'Equity', 'Money Market'] as const;

// The design's row names for the report's categories; other categories show as the report names them.
export const INCOME_CATEGORY_LABELS: Partial<Record<string, string>> = {
    Bond: 'Fixed Income',
    Equity: 'Equities',
    'Money Market': 'Cash & Equivalents',
};

export interface IncomeQuarter {
    /** ISO date (yyyy-mm-dd) of the first day of the quarter; the column key. */
    periodStart: string;
    /** Column header, e.g. "Q3 2026". */
    label: string;
}

export interface IncomeRow {
    category: string;
    /** One amount per quarter, in the order of `quarters`; 0 where the report has no cell. */
    amounts: number[];
    total: number;
}

export interface IncomeMatrix {
    quarters: IncomeQuarter[];
    rows: IncomeRow[];
    /** Per-quarter sums across the rows, in the order of `quarters`. */
    columnTotals: number[];
    grandTotal: number;
}

/**
 * Calendar quarter label of an ISO date, parsed from the string so the browser's time zone
 * cannot shift the date.
 *   "2026-07-01" -> "Q3 2026"
 */
export const quarterLabel = (isoDate: string): string => {
    const [year, month] = isoDate.split('-').map(Number);
    return `Q${Math.floor((month - 1) / 3) + 1} ${year}`;
};

const categoryRank = (category: string): number => {
    const index = (INCOME_CATEGORY_ORDER as readonly string[]).indexOf(category);
    return index === -1 ? INCOME_CATEGORY_ORDER.length : index;
};

/**
 * Pivots the report's category x quarter entries into the table the design shows: one row per
 * category in the fixed order, one column per quarter in date order, totals on both axes.
 * Unknown amounts count as zero, the same way the service sums them.
 */
export const buildIncomeMatrix = (summary: readonly ProjectedIncomeSummary[]): IncomeMatrix => {
    const quarterStarts = [...new Set(summary.map((entry) => entry.periodStart))].sort();
    const quarters = quarterStarts.map((periodStart) => ({ periodStart, label: quarterLabel(periodStart) }));

    const categories = [...new Set(summary.map((entry) => entry.category))].sort(
        (a, b) => categoryRank(a) - categoryRank(b) || a.localeCompare(b),
    );

    const rows = categories.map((category) => {
        const amounts = quarterStarts.map((periodStart) =>
            summary
                .filter((entry) => entry.category === category && entry.periodStart === periodStart)
                .reduce((sum, entry) => sum + (toNumber(entry.amount) ?? 0), 0),
        );
        return { category, amounts, total: amounts.reduce((sum, amount) => sum + amount, 0) };
    });

    const columnTotals = quarterStarts.map((_, i) => rows.reduce((sum, row) => sum + row.amounts[i], 0));

    return {
        quarters,
        rows,
        columnTotals,
        grandTotal: columnTotals.reduce((sum, amount) => sum + amount, 0),
    };
};

/**
 * The planned withdrawals of the reporting scope, listed the way the Investment Policy's
 * Withdrawal Plan lists them. An account is in scope when it is reportable and, if accounts are
 * selected, one of them (no selection = the whole household). Per account group, the group's
 * in-scope accounts decide which withdrawals count (getPolicyWithdrawals): those drawn from one
 * of those accounts, plus the group's "Any Account" withdrawals when the group is portfolio-level.
 * A group with no account in scope contributes nothing. Only the upcoming withdrawals are kept, in
 * the Withdrawal Plan's order (upcomingPolicyWithdrawals).
 */
export const getScopedWithdrawals = (
    profiles: readonly AccountGroupProfileWithAumIncluded[],
    configs: CashFlowConfig[],
    selectedAccountGUIDs: readonly string[],
    includeClosed: boolean,
    today = startOfDay(new Date()),
): CashFlowConfig[] => {
    const inScope = (account: AccountWithAumIncluded) =>
        isReportableAccount(account, includeClosed) &&
        (selectedAccountGUIDs.length === 0 || selectedAccountGUIDs.includes(account.accountGUID));

    const withdrawals = profiles.flatMap((profile) => {
        const accounts = profile.accounts.filter(inScope);
        return accounts.length ? getPolicyWithdrawals(configs, accounts, profile.accountGroup) : [];
    });

    // An account can sit in more than one group profile, which would list its withdrawals twice.
    return upcomingPolicyWithdrawals(uniqBy(withdrawals, 'cashFlowConfigGUID'), today);
};
